import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import type { ActWithParticipants } from "@shared/schema";

interface ActSelectorProps {
  organisationId: number;
  selectedActId?: number;
  onSelectAct: (act: ActWithParticipants | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export function ActSelector({ 
  organisationId, 
  selectedActId, 
  onSelectAct,
  label = "Select Act",
  placeholder = "Search for acts...",
  required = false
}: ActSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAct, setSelectedAct] = useState<ActWithParticipants | null>(null);
  
  // Detect browser for transparency handling
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Fetch acts
  const { data: acts = [] } = useQuery<ActWithParticipants[]>({
    queryKey: [`/api/acts?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/acts?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch acts");
      return response.json();
    },
  });

  // Set selected act when acts are loaded and selectedActId is provided
  useEffect(() => {
    if (selectedActId && acts.length > 0) {
      const act = acts.find(a => a.id === selectedActId);
      if (act) {
        setSelectedAct(act);
      }
    }
  }, [selectedActId, acts]);

  // Filter acts based on search term
  const filteredActs = acts
    .filter(act => act.active !== false) // Only show active acts
    .filter(act => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      const actName = act.name || act.participants?.[0]?.participant.name || "";
      
      // Search in act name
      if (actName.toLowerCase().includes(search)) return true;
      
      // Search in participant names
      return act.participants?.some(ap => 
        ap.participant.name.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const aName = a.name || a.participants?.[0]?.participant.name || "";
      const bName = b.name || b.participants?.[0]?.participant.name || "";
      return aName.localeCompare(bName);
    });

  const handleSelectAct = (act: ActWithParticipants) => {
    setSelectedAct(act);
    onSelectAct(act);
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleRemoveAct = () => {
    setSelectedAct(null);
    onSelectAct(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const getActDisplayName = (act: ActWithParticipants) => {
    return act.name || act.participants?.[0]?.participant.name || "Unknown Act";
  };

  return (
    <div>
      <Label>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      {/* Selected act display */}
      {selectedAct && (
        <div className="mb-3">
          <Badge variant="secondary" className="py-1">
            {getActDisplayName(selectedAct)}
            {selectedAct.participants && selectedAct.participants.length > 1 && selectedAct.name && (
              <span className="ml-1 text-xs">
                ({selectedAct.participants.map(p => p.participant.name).join(", ")})
              </span>
            )}
            <button
              type="button"
              onClick={handleRemoveAct}
              className="ml-2 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        </div>
      )}

      {/* Search input */}
      {!selectedAct && (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={placeholder}
              className="pl-10"
            />
          </div>

          {/* Dropdown Results */}
          {showDropdown && filteredActs.length > 0 && (
            <div 
              className={cn(
                "absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border",
                isChrome || isMobileSafari
                  ? 'bg-white border-gray-200' 
                  : 'bg-white/95 backdrop-blur-xl border-white/30'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {filteredActs.map((act) => (
                <div
                  key={act.id}
                  onClick={() => handleSelectAct(act)}
                  className="px-4 py-3 hover:bg-white/40 cursor-pointer border-b border-white/30 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">
                        {getActDisplayName(act)}
                      </div>
                      {act.participants && act.participants.length > 0 && act.name && (
                        <div className="text-sm text-slate-500">
                          {act.participants.map(p => p.participant.name).join(", ")}
                        </div>
                      )}
                      {act.genre && act.genre.length > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          {act.genre.join(", ")}
                        </div>
                      )}
                    </div>
                    {act.rating && (
                      <div className="text-sm text-yellow-600">
                        ★ {act.rating}/5
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results message */}
          {showDropdown && searchTerm && filteredActs.length === 0 && (
            <div 
              className={cn(
                "absolute z-50 w-full mt-1 rounded-xl shadow-2xl border p-4",
                isChrome || isMobileSafari
                  ? 'bg-white border-gray-200' 
                  : 'bg-white/95 backdrop-blur-xl border-white/30'
              )}
            >
              <p className="text-sm text-slate-500">No acts found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}